import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { getCurrentUser, getCachedUser, signOut, uploadProfilePicture, removeAvatar, isAuthenticated } from '../utils/auth';
import { getVerificationStatus } from '../utils/verification';
import { getNotifications, markNotificationAsRead, subscribeToNotifications } from '../utils/notifications';
import { showToast } from '../components/ToastContainer';
import BuyerOrders from '../components/BuyerOrders';
import SellerOrders from '../components/SellerOrders';
import { getBalance } from '../utils/balance';
import NotificationLottieIcon from '../components/NotificationLottieIcon';
import MessagesTab from '../components/MessagesTab';
import WalletTab from '../components/WalletTab';
import BoostingManagement from '../components/BoostingManagement';
import OffersTab from '../components/OffersTab';
import ProfileFeedbackTab from '../components/ProfileFeedbackTab';
import VerifiedSellerBadge from '../components/VerifiedSellerBadge';
import { NotificationSkeleton, Skeleton } from '../components/common/Skeleton';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/ProfilePage.css';
import '../styles/DsProfile.css';

const NotificationsTab = ({ userId }) => {
  const { t } = useTranslation();
  // Try to load cached notifications first
  const getCachedNotifications = () => {
    if (!userId) return [];
    try {
      const cached = localStorage.getItem(`notifications_${userId}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Only use cache if it's less than 5 minutes old
        if (parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
          return parsed.data || [];
        }
      }
    } catch (error) {
      console.error('Error reading cached notifications:', error);
    }
    return [];
  };

  const cachedNotifications = getCachedNotifications();
  const [notifications, setNotifications] = useState(cachedNotifications);
  const [loading, setLoading] = useState(!cachedNotifications.length);

  useEffect(() => {
    if (!userId) return;

    const loadNotifications = async () => {
      // Only show loading if we don't have cached data
      if (cachedNotifications.length === 0) {
        setLoading(true);
      }
      
      const { data } = await getNotifications(userId);
      if (data) {
        setNotifications(data);
        // Cache notifications for instant loading next time
        try {
          localStorage.setItem(`notifications_${userId}`, JSON.stringify({
            data,
            timestamp: Date.now()
          }));
        } catch (error) {
          console.error('Error caching notifications:', error);
        }
      }
      setLoading(false);
    };

    loadNotifications();

    // Subscribe to real-time notifications
    const unsubscribe = subscribeToNotifications(userId, (newNotification) => {
      setNotifications(prev => [newNotification, ...prev]);
    });

    return () => {
      unsubscribe();
    };
  }, [userId]);

  const handleMarkAsRead = async (notificationId) => {
    await markNotificationAsRead(notificationId);
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Show skeleton only if loading and no cached data
  const showSkeleton = loading && notifications.length === 0;

  if (showSkeleton) {
    return (
      <div className="notifications-list">
        {[1, 2, 3, 4, 5].map(i => (
          <NotificationSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (notifications.length === 0) {
    return <div className="profile-empty-state">{t('profile.noNotificationsYet') || 'No notifications yet.'}</div>;
  }

  const NotificationItemFull = ({ notification }) => {
    const [isHovered, setIsHovered] = useState(false);
    
    return (
      <div
        className={`notification-item-full ${!notification.read ? 'unread' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="notification-item-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="notification-icon">
              <NotificationLottieIcon notification={notification} size={32} isParentHovered={isHovered} />
            </div>
            <div className="notification-item-title">{notification.title}</div>
          </div>
          {!notification.read && (
            <button
              className="mark-read-btn-small"
              onClick={() => handleMarkAsRead(notification.id)}
            >
              {t('profile.markAsRead') || 'Mark as read'}
            </button>
          )}
        </div>
        <div className="notification-item-message">{notification.message}</div>
        <div className="notification-item-time">{formatTime(notification.created_at)}</div>
      </div>
    );
  };

  return (
    <div className="notifications-list">
      {notifications.map((notification) => (
        <NotificationItemFull key={notification.id} notification={notification} />
      ))}
    </div>
  );
};

function ProfilePageContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const { t } = useTranslation();
  
  // Initialize with cached user data for instant display
  const cachedUser = getCachedUser();
  const [user, setUser] = useState(cachedUser);
  const [loading, setLoading] = useState(!cachedUser);
  
  // Determine active tab from URL path or query param
  const getTabFromPath = () => {
    const path = location.pathname;
    
    // Map paths to tab IDs
    if (path === '/profile/account' || path === '/profile') return 'account';
    if (path === '/profile/wallet') return 'wallet';
    if (path === '/profile/messages') return 'messages';
    if (path === '/profile/notifications') return 'notifications';
    if (path === '/profile/become-seller') return 'become-seller';
    if (path === '/profile/offers/currency') return 'offers-currency';
    if (path === '/profile/offers/accounts') return 'offers-accounts';
    if (path === '/profile/offers/top-ups') return 'offers-topups';
    if (path === '/profile/offers/items') return 'offers-items';
    if (path === '/profile/offers') return 'offers-currency'; // Default to currency
    if (path === '/profile/seller/purchased-orders') return 'seller-purchased-orders';
    if (path === '/profile/seller/sold-orders') return 'seller-sold-orders';
    if (path === '/profile/seller/my-items') return 'seller-my-items';
    if (path === '/profile/seller/requests') return 'seller-requests';
    if (path === '/profile/seller') return 'seller-purchased-orders'; // Default to purchased orders
    if (path === '/profile/boosting/my-requests') return 'my-boosting-requests';
    if (path === '/profile/boosting/received') return 'received-boosting-requests';
    if (path === '/profile/boosting') return 'received-boosting-requests'; // Default to received
    if (path === '/profile/feedback') return 'feedback';
    if (path === '/profile/view') return 'profile';
    if (path === '/profile/orders/purchased') return 'purchased-orders';
    if (path === '/profile/orders/sold') return 'sold-orders';
    if (path === '/profile/orders') return 'purchased-orders'; // Default to purchased
    
    return null;
  };
  
  // Check if tab is specified in URL path or query param
  const tabFromPath = getTabFromPath();
  const tabFromUrl = searchParams.get('tab');
  // Map old 'orders' tab to 'purchased-orders' for backward compatibility
  const initialTab = tabFromPath || (tabFromUrl === 'orders' ? 'purchased-orders' : (tabFromUrl || 'account'));
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Update tab if URL changes
  useEffect(() => {
    const newTab = getTabFromPath();
    const urlTab = searchParams.get('tab');
    
    if (newTab) {
      setActiveTab(newTab);
    } else if (urlTab) {
      // Map old tab names to new ones
      if (urlTab === 'orders') {
        setActiveTab('purchased-orders');
      } else if (urlTab === 'boosting') {
        setActiveTab('received-boosting-requests');
      } else {
        setActiveTab(urlTab);
      }
    }
  }, [location.pathname, searchParams]);
  const [editing, setEditing] = useState(false);
  const [username, setUsername] = useState(cachedUser?.username || '');
  const [email, setEmail] = useState(cachedUser?.email || '');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isSeller, setIsSeller] = useState(cachedUser?.is_seller || false);
  const [verificationStatus, setVerificationStatus] = useState(cachedUser?.veriff_status || null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationFormVisible, setVerificationFormVisible] = useState(false);
  const [balance, setBalance] = useState(null);
  const veriffInstanceRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.documentElement.style.overflow = '';
  }, []);

  const handleSyncStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        alert('Not authenticated');
        return;
      }

      const response = await fetch('/api/verification/sync-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.error || 'Failed to sync status');
        return;
      }

      // Update local state
      setIsSeller(data.isSeller);
      setVerificationStatus(data.status);
      
      // Refresh user data
      const { user: updatedUser } = await getCurrentUser();
      if (updatedUser) {
        setUser(updatedUser);
        setIsSeller(updatedUser.is_seller);
        setVerificationStatus(updatedUser.veriff_status);
      }

      alert(`Status synced: ${data.status}`);
    } catch (error) {
      console.error('Sync status error:', error);
      alert('Failed to sync status');
    }
  };

  const handleRefreshStatus = async () => {
    console.log('🔄 Manually refreshing verification status...');
    
    // Refresh user data first (this gets the latest from database)
    const { user: updatedUser } = await getCurrentUser();
    if (updatedUser) {
      setUser(updatedUser);
      setIsSeller(updatedUser.is_seller);
      setVerificationStatus(updatedUser.veriff_status);
      console.log('✅ User data refreshed, status:', updatedUser.veriff_status, 'isSeller:', updatedUser.is_seller);
    }
    
    // Also get verification status from API
    const { data: statusData } = await getVerificationStatus();
    if (statusData) {
      setIsSeller(statusData.isSeller);
      setVerificationStatus(statusData.status);
      console.log('✅ Verification status refreshed:', statusData.status, 'isSeller:', statusData.isSeller);
    }
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/auth?signin=true');
      return;
    }

    // If we already have cached user data, UI is already showing it
    // Just fetch fresh data in background
    if (cachedUser) {
      // Clear any avatar preview on page load - preview should only show when selecting a new file
      setAvatarPreview(null);
    } else {
      setLoading(false);
      // Clear avatar preview on initial load
      setAvatarPreview(null);
    }

    // Fetch fresh data in background and update if different
    // Use a ref to prevent multiple simultaneous calls
    let cancelled = false;
    
    const loadUser = async () => {
      const { user: currentUser, error } = await getCurrentUser();
      if (cancelled) return;
      
      if (error) {
        // Only redirect if we don't have cached data
        if (!cachedUser) {
          navigate('/auth?signin=true');
        }
        return;
      }
      
      if (currentUser) {
        // Update state with fresh data
        setUser(currentUser);
        setUsername(currentUser.username || '');
        setEmail(currentUser.email || '');
        
        // Update seller status and verification status from user data
        if (currentUser.is_seller !== undefined) {
          setIsSeller(currentUser.is_seller);
        }
        if (currentUser.veriff_status !== undefined) {
          setVerificationStatus(currentUser.veriff_status);
        }
        
        // Don't load cached avatar as preview - only use it for display
      }
    };
    
    loadUser();
    
    return () => {
      cancelled = true;
    };

    // Check if user was redirected from Veriff verification
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('verification') === 'complete') {
      // Refresh verification status after redirect (webhook may take a few seconds)
      // Poll multiple times to catch webhook updates
      let pollCount = 0;
      const maxPolls = 15; // Poll for up to 30 seconds
      
      const pollStatus = setInterval(async () => {
        pollCount++;
        console.log(`🔄 Polling verification status (${pollCount}/${maxPolls})...`);
        const { data: statusData } = await getVerificationStatus();
        if (statusData) {
          console.log('📊 Current status:', statusData);
          setIsSeller(statusData.isSeller);
          setVerificationStatus(statusData.status);
          
          // Stop polling if approved or rejected
          if (statusData.status === 'approved' || statusData.status === 'rejected') {
            clearInterval(pollStatus);
            console.log('✅ Status updated, stopped polling');
            // Refresh user data to get latest seller status
            const { user: updatedUser } = await getCurrentUser();
            if (updatedUser) {
              setUser(updatedUser);
              setIsSeller(updatedUser.is_seller);
              setVerificationStatus(updatedUser.veriff_status);
            }
          }
        }
        
        if (pollCount >= maxPolls) {
          clearInterval(pollStatus);
          console.log('⏰ Stopped polling after max attempts');
        }
      }, 2000); // Poll every 2 seconds
      
      // Clean up URL
      window.history.replaceState({}, '', '/profile');
    }

    // Fetch fresh data in background
    loadUser();
    
    // Load balance
    const loadBalance = async () => {
      try {
        const balanceData = await getBalance();
        if (!cancelled) {
          setBalance(balanceData);
        }
      } catch (error) {
        console.error('Failed to load balance:', error);
      }
    };
    loadBalance();
    
    // Load verification status in background (don't block UI)
    // Cached status is already shown from initial state
    const loadVerificationStatus = async () => {
      const { data: statusData } = await getVerificationStatus();
      if (cancelled) return null;
      if (statusData) {
        setIsSeller(statusData.isSeller);
        setVerificationStatus(statusData.status);
        return statusData.status;
      }
      return null;
    };
    
    // Load verification status in background (non-blocking)
    // UI already shows cached status, this just refreshes it
    loadVerificationStatus().then((initialStatus) => {
      if (cancelled) return;
      // If status is pending, poll periodically to catch webhook updates
      if (initialStatus === 'pending') {
        console.log('🔄 Status is pending, starting periodic polling...');
        const pollInterval = setInterval(async () => {
          if (cancelled) {
            clearInterval(pollInterval);
            return;
          }
          const currentStatus = await loadVerificationStatus();
          if (cancelled) {
            clearInterval(pollInterval);
            return;
          }
          if (currentStatus && currentStatus !== 'pending') {
            // Status changed from pending, stop polling and refresh user data
            clearInterval(pollInterval);
            console.log('✅ Status changed to:', currentStatus);
            const { user: updatedUser } = await getCurrentUser();
            if (updatedUser && !cancelled) {
              setUser(updatedUser);
              setIsSeller(updatedUser.is_seller);
              setVerificationStatus(updatedUser.veriff_status);
            }
          }
        }, 5000); // Poll every 5 seconds
        
        // Clean up interval on unmount
        return () => {
          clearInterval(pollInterval);
        };
      }
    });
  }, [navigate]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert(t('profile.pleaseSelectImage') || 'Please select an image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        alert(t('profile.profilePictureDescription') || 'Image size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveAvatar = async () => {
    if (!fileInputRef.current?.files[0]) {
      showToast(t('profile.pleaseSelectImage') || 'Please select an image', 'warning');
      return;
    }

    const file = fileInputRef.current.files[0];
    const { error } = await uploadProfilePicture(user.id, file);
    
    if (error) {
      showToast(error.message || t('profile.failedToUploadAvatar') || 'Failed to upload avatar', 'error');
    } else {
      showToast(t('profile.avatarUpdatedSuccessfully') || 'Avatar updated successfully', 'success');
      // Clear preview and file input immediately
      setAvatarPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Reload user data (realtime will also update it)
      const { user: updatedUser } = await getCurrentUser();
      if (updatedUser) {
        setUser(updatedUser);
        // Update seller status and verification status to keep state in sync
        if (updatedUser.is_seller !== undefined) {
          setIsSeller(updatedUser.is_seller);
        }
        if (updatedUser.veriff_status !== undefined) {
          setVerificationStatus(updatedUser.veriff_status);
        }
        // Update cached avatar with the actual URL from server
        if (updatedUser.avatar_url && !updatedUser.avatar_url.startsWith('letter:')) {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(`avatar_${user.id}`, updatedUser.avatar_url);
          }
        }
      }
    }
  };

  const handleRemoveAvatar = async () => {
    const { error } = await removeAvatar(user.id);
    
    if (error) {
      showToast(error.message || t('profile.failedToRemoveAvatar') || 'Failed to remove avatar', 'error');
    } else {
      showToast(t('profile.avatarRemovedSuccessfully') || 'Avatar removed successfully', 'success');
      setAvatarPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Clear cached avatar
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(`avatar_${user.id}`);
      }
      // Reload user data (realtime will also update it)
      const { user: updatedUser } = await getCurrentUser();
      if (updatedUser) {
        setUser(updatedUser);
        // Update seller status and verification status to keep state in sync
        if (updatedUser.is_seller !== undefined) {
          setIsSeller(updatedUser.is_seller);
        }
        if (updatedUser.veriff_status !== undefined) {
          setVerificationStatus(updatedUser.veriff_status);
        }
      }
    }
  };

  const handleSaveProfile = async () => {
    // TODO: Implement API call to update username/email
    setEditing(false);
    showToast(t('profile.profileUpdatedSuccessfully') || 'Profile updated successfully', 'success');
  };

  const handleLogout = async () => {
    // Show loading state immediately to prevent flash
    setLoading(true);
    
    try {
      await signOut();
      
      // Small delay to ensure localStorage is cleared before redirect
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Navigate first, then reload to prevent grey screen
      navigate('/', { replace: true });
      
      // Use setTimeout to ensure navigation completes before reload
      setTimeout(() => {
        window.location.href = '/';
      }, 50);
    } catch (error) {
      console.error('Logout error:', error);
      // Even on error, clear and redirect
      navigate('/', { replace: true });
      setTimeout(() => {
        window.location.href = '/';
      }, 50);
    }
  };

  const loadVeriffSDK = () => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (typeof window.Veriff !== 'undefined' && typeof window.veriffSDK !== 'undefined') {
        console.log('✅ Veriff SDK already loaded');
        resolve();
        return;
      }

      // Check if scripts are already being loaded
      if (document.querySelector('script[src*="veriff.me"]')) {
        console.log('Veriff scripts already in DOM, waiting...');
        let retries = 0;
        const check = setInterval(() => {
          if (typeof window.Veriff !== 'undefined' && typeof window.veriffSDK !== 'undefined') {
            clearInterval(check);
            console.log('✅ Veriff SDK loaded');
            resolve();
          } else if (retries++ > 20) {
            clearInterval(check);
            reject(new Error('SDK timeout'));
          }
        }, 500);
        return;
      }

      console.log('Loading Veriff SDK scripts...');
      
      // Load first script
      const script1 = document.createElement('script');
      script1.src = 'https://cdn.veriff.me/sdk/js/1.5/veriff.min.js';
      script1.async = true;
      
      script1.onload = () => {
        console.log('✅ Script 1 loaded');
        // Load second script
        const script2 = document.createElement('script');
        script2.src = 'https://cdn.veriff.me/incontext/js/v1/veriff.js';
        script2.async = true;
        
        script2.onload = () => {
          console.log('✅ Script 2 loaded');
          // Wait a bit for SDK to initialize
          setTimeout(() => {
            if (typeof window.Veriff !== 'undefined' && typeof window.veriffSDK !== 'undefined') {
              console.log('✅ Veriff SDK ready');
              resolve();
            } else {
              console.error('❌ SDK scripts loaded but SDK not available');
              reject(new Error('SDK not available after loading scripts'));
            }
          }, 500);
        };
        
        script2.onerror = () => {
          console.error('❌ Failed to load script 2');
          reject(new Error('Failed to load Veriff incontext script'));
        };
        
        document.head.appendChild(script2);
      };
      
      script1.onerror = () => {
        console.error('❌ Failed to load script 1');
        reject(new Error('Failed to load Veriff SDK script'));
      };
      
      document.head.appendChild(script1);
    });
  };

  const handleStartVerification = async () => {
    setVerificationLoading(true);
    console.log('🚀 Starting Veriff verification...');
    
    try {
      // First, create a verification session via backend API
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showToast('Please log in to start verification', 'error');
        setVerificationLoading(false);
        return;
      }

      console.log('📡 Creating verification session via backend...');
      const sessionResponse = await fetch('/api/verification/create-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!sessionResponse.ok) {
        const errorData = await sessionResponse.json();
        console.error('❌ Failed to create session:', errorData);
        showToast(errorData.error || 'Failed to create verification session', 'error');
        setVerificationLoading(false);
        return;
      }

      const sessionData = await sessionResponse.json();
      console.log('✅ Session created:', sessionData);

      if (!sessionData.sessionId || !sessionData.url) {
        console.error('❌ Invalid session response:', sessionData);
        showToast('Invalid session response from server', 'error');
        setVerificationLoading(false);
        return;
      }

      // Load SDK if not already loaded
      await loadVeriffSDK();
      
      console.log('✅ SDK loaded, initializing...');
      
      // Show the form container immediately so veriff-root div exists
      setVerificationFormVisible(true);
      
      // Wait a moment for React to render the div
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Find veriff-root div (should now be rendered by React)
      let veriffRoot = document.getElementById('veriff-root');
      
      if (!veriffRoot) {
        console.error('❌ veriff-root div not found after setting visible');
        alert('Failed to initialize verification form. Please try again.');
        setVerificationLoading(false);
        setVerificationFormVisible(false);
        return;
      }
      
      // Make sure it's visible and has proper styling
      veriffRoot.style.display = 'block';
      veriffRoot.style.width = '100%';
      veriffRoot.style.minHeight = '600px';
      veriffRoot.style.height = 'auto';
      veriffRoot.style.padding = '2rem';
      veriffRoot.style.backgroundColor = '#141414';
      veriffRoot.style.border = '1px solid #252525';
      veriffRoot.style.borderRadius = '8px';
      veriffRoot.style.position = 'relative';
      veriffRoot.style.zIndex = '1';
      veriffRoot.style.overflow = 'visible';
      
      console.log('✅ veriff-root div ready:', veriffRoot);
      
      // Add class to body/html to maintain background styling
      document.body.classList.add('veriff-active');
      document.documentElement.classList.add('veriff-active');
      const profileWrapper = document.querySelector('.profile-page-wrapper');
      const profilePage = document.querySelector('.profile-page');
      const profileSection = document.querySelector('.profile-section');
      if (profileWrapper) profileWrapper.classList.add('veriff-active');
      if (profilePage) profilePage.classList.add('veriff-active');
      if (profileSection) profileSection.classList.add('veriff-active');

      // Open Veriff verification in iframe using the URL from backend
      console.log('🖼️ Creating Veriff frame with URL:', sessionData.url);
      
      if (typeof window.veriffSDK !== 'undefined' && window.veriffSDK.createVeriffFrame) {
        const veriffFrame = window.veriffSDK.createVeriffFrame({ 
          url: sessionData.url,
          onEvent: function(event) {
            console.log('📋 Veriff event:', event);
            // Handle different Veriff events
            if (event === 'FINISHED') {
              // Verification completed, refresh status
              setTimeout(() => {
                handleCancelVerification(); // Clean up first
                window.location.reload();
              }, 2000);
            } else if (event === 'CANCELED' || event === 'CANCELLED') {
              // User canceled verification
              console.log('🚫 User canceled verification');
              handleCancelVerification();
              showToast('Verification canceled', 'info');
            } else if (event === 'ERROR') {
              // Error occurred
              console.error('❌ Veriff error event');
              handleCancelVerification();
              showToast('An error occurred during verification', 'error');
            }
          }
        });
        
        // Store reference for cleanup
        veriffInstanceRef.current = veriffFrame;
        
        // Wait a bit for iframe to be created, then apply additional styling
        setTimeout(() => {
          const veriffIframes = document.querySelectorAll('iframe[src*="veriff"]');
          veriffIframes.forEach(iframe => {
            iframe.style.backgroundColor = '#141414';
            iframe.style.border = '1px solid #252525';
            iframe.style.borderRadius = '8px';
            iframe.style.width = '100%';
            iframe.style.minHeight = '600px';
          });
        }, 500);
        
        console.log('✅ Veriff frame created');
        setVerificationLoading(false);
      } else {
        console.error('❌ veriffSDK.createVeriffFrame not available, redirecting...');
        // Fallback: redirect to URL
        window.location.href = sessionData.url;
      }
    } catch (error) {
      console.error('Veriff initialization error:', error);
      showToast(`Failed to initialize verification: ${error.message}`, 'error');
      setVerificationLoading(false);
      setVerificationFormVisible(false);
    }
  };

  const handleCancelVerification = () => {
    console.log('🚫 Canceling verification...');
    
    // Destroy Veriff instance if it exists
    if (veriffInstanceRef.current) {
      try {
        // Try to destroy/close the Veriff instance
        if (typeof veriffInstanceRef.current.destroy === 'function') {
          veriffInstanceRef.current.destroy();
        } else if (typeof veriffInstanceRef.current.close === 'function') {
          veriffInstanceRef.current.close();
        }
      } catch (error) {
        console.warn('Error destroying Veriff instance:', error);
      }
      veriffInstanceRef.current = null;
    }
    
    // Clear any Veriff iframes or modals
    const veriffIframes = document.querySelectorAll('iframe[src*="veriff"], .veriff-frame, .veriff-modal');
    veriffIframes.forEach(iframe => {
      try {
        iframe.remove();
      } catch (error) {
        console.warn('Error removing Veriff iframe:', error);
      }
    });
    
    // Clear the form
    const veriffRoot = document.getElementById('veriff-root');
    if (veriffRoot) {
      veriffRoot.textContent = ''; // Use textContent instead of innerHTML for security
      veriffRoot.style.display = 'none';
    }
    
    // Remove active classes
    document.body.classList.remove('veriff-active');
    document.documentElement.classList.remove('veriff-active');
    const profileWrapper = document.querySelector('.profile-page-wrapper');
    const profilePage = document.querySelector('.profile-page');
    const profileSection = document.querySelector('.profile-section');
    if (profileWrapper) profileWrapper.classList.remove('veriff-active');
    if (profilePage) profilePage.classList.remove('veriff-active');
    if (profileSection) profileSection.classList.remove('veriff-active');
    
    // Reset states
    setVerificationLoading(false);
    setVerificationFormVisible(false);
    
    // Don't change verificationStatus - only webhook should update it
    console.log('✅ Verification canceled, ready to start again');
  };

  const handleResetVerification = async () => {
    if (verificationStatus !== 'pending') {
      showToast('Can only reset verification when status is pending', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to reset your verification? This will clear your pending verification and allow you to start a new one.')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showToast('Please log in to reset verification', 'error');
        return;
      }

      const response = await fetch('/api/verification/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showToast(data.error || 'Failed to reset verification', 'error');
        return;
      }

      // Update local state
      setVerificationStatus(null);
      setIsSeller(false);
      
      // Refresh user data
      const { user: updatedUser } = await getCurrentUser();
      if (updatedUser) {
        setUser(updatedUser);
        setIsSeller(updatedUser.is_seller);
        setVerificationStatus(updatedUser.veriff_status);
      }

      showToast('Verification reset successfully', 'success');
    } catch (error) {
      console.error('Reset verification error:', error);
      showToast('Failed to reset verification', 'error');
    }
  };

  const getAvatarDisplay = () => {
    if (avatarPreview) {
      return <img src={avatarPreview} alt={user?.username || 'User'} className="profile-avatar-image" />;
    }
    
    if (user?.avatar_url && !user.avatar_url.startsWith('letter:')) {
      return <img src={user.avatar_url} alt={user?.username || 'User'} className="profile-avatar-image" />;
    }
    
    const letter = user?.username?.charAt(0).toUpperCase() || 'U';
    return (
      <div className="profile-avatar-letter">
        {letter}
      </div>
    );
  };

  const getDashboardMeta = () => {
    const meta = {
      account: {
        title: t('profile.accountSettings', 'Account settings'),
        description: t('profile.accountSettingsDesc', 'Update your email, username, password, and profile picture.'),
      },
      security: {
        title: t('profile.security', 'Security'),
        description: t('profile.securityDesc', 'Protect your account and review security options.'),
      },
      'purchased-orders': {
        title: t('profile.purchasedOrders', 'Purchased orders'),
        description: t('profile.purchasedOrdersDesc', "Track and manage everything you've bought."),
      },
      'sold-orders': {
        title: t('profile.soldOrders', 'Sold orders'),
        description: t('profile.soldOrdersDesc', 'Fulfill and review your completed sales.'),
      },
      notifications: {
        title: t('profile.notifications', 'Notifications'),
        description: t('profile.notificationsDesc', 'Stay on top of orders, messages, and account alerts.'),
      },
      wallet: {
        title: t('profile.wallet', 'Wallet'),
        description: t('profile.walletDesc', 'Check your balance, deposits, and withdrawals.'),
      },
      'offers-currency': {
        title: t('profile.offersCurrency', 'Currency offers'),
        description: t('profile.offersDesc', 'Manage your listed offers.'),
      },
      'offers-accounts': {
        title: t('profile.offersAccounts', 'Account offers'),
        description: t('profile.offersDesc', 'Manage your listed offers.'),
      },
      'offers-topups': {
        title: t('profile.offersTopups', 'Top-up offers'),
        description: t('profile.offersDesc', 'Manage your listed offers.'),
      },
      'offers-items': {
        title: t('profile.offersItems', 'Item offers'),
        description: t('profile.offersDesc', 'Manage your listed offers.'),
      },
      'seller-purchased-orders': {
        title: t('profile.sellerPurchased', 'Seller · Purchased orders'),
        description: t('profile.sellerOrdersDesc', 'Orders linked to your seller activity.'),
      },
      'seller-sold-orders': {
        title: t('profile.sellerSold', 'Seller · Sold orders'),
        description: t('profile.sellerOrdersDesc', 'Orders linked to your seller activity.'),
      },
      'seller-my-items': {
        title: t('profile.myItems', 'My items'),
        description: t('profile.myItemsDesc', 'Browse and edit your active listings.'),
      },
      'seller-requests': {
        title: t('profile.requests', 'Requests'),
        description: t('profile.requestsDesc', 'Respond to buyer requests for your services.'),
      },
      'become-seller': {
        title: t('profile.sellerVerification', 'Seller verification'),
        description: t('profile.sellerVerificationDesc', 'Verify your identity to unlock selling on Bazaar.gg.'),
      },
      'my-boosting-requests': {
        title: t('profile.myRequests', 'My boosting requests'),
        description: t('profile.boostingDesc', "Create and track boosting jobs you've posted."),
      },
      'received-boosting-requests': {
        title: t('profile.receivedRequests', 'Received boosting requests'),
        description: t('profile.boostingReceivedDesc', 'Browse requests you can take on as a booster.'),
      },
      boosting: {
        title: t('profile.boosting', 'Boosting'),
        description: t('profile.boostingDesc', 'Manage boosting requests and subscriptions.'),
      },
      feedback: {
        title: t('profile.feedback', 'Feedback'),
        description: t('profile.feedbackDesc', "Reviews you've given and received."),
      },
      messages: {
        title: t('profile.messages', 'Messages'),
        description: t('profile.messagesDesc', 'Chat with buyers and sellers about your orders.'),
      },
      profile: {
        title: t('profile.viewProfile', 'View profile'),
        description: t('profile.viewProfileDesc', 'Preview how others see your public profile.'),
      },
    };
    return meta[activeTab] || {
      title: t('profile.dashboard', 'Dashboard'),
      description: '',
    };
  };

  if (loading) {
    return (
      <div className="profile-page-wrapper">
        <div className="profile-skeleton">
          <Skeleton className="skeleton-avatar-large" style={{ width: '80px', height: '80px', borderRadius: '16px', marginBottom: '1.5rem' }} />
          <Skeleton style={{ width: '200px', height: '24px', marginBottom: '0.5rem' }} />
          <Skeleton style={{ width: '150px', height: '16px', marginBottom: '2rem' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} style={{ width: '100%', height: '48px', borderRadius: '10px' }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const dashMeta = getDashboardMeta();

  return (
    <div className="profile-page-wrapper">
      <div className={`profile-page${activeTab === 'messages' ? ' profile-page--messages' : ''}`}>
        <header className="profile-dash-header">
          <div className="ds-sec-copy">
            <h1 className="profile-dash-title">{dashMeta.title}</h1>
            {dashMeta.description ? (
              <p className="profile-dash-desc">{dashMeta.description}</p>
            ) : null}
          </div>
        </header>
        <div className="profile-main">
            {activeTab === 'become-seller' && (
              <div className="profile-section">
                
                {isSeller ? (
                  <div className="seller-status-approved">
                    <div className="seller-badge">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Verified Seller</span>
                    </div>
                    <p className="seller-status-text">
                      You are a verified seller! You can now list products and manage your seller account.
                    </p>
                    {verificationStatus === 'approved' && (
                      <div className="seller-info">
                        <p>Status: <strong>Approved</strong></p>
                      </div>
                    )}
                  </div>
                ) : verificationStatus === 'pending' ? (
                  <div className="seller-status-pending">
                    <div className="seller-badge pending">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="#f59e0b" strokeWidth="2"/>
                        <path d="M12 8v4l3 3" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <span>Verification Pending</span>
                    </div>
                    <p className="seller-status-text">
                      Your verification is being reviewed. We'll notify you once it's complete.
                    </p>
                    <p className="seller-status-note">
                      This usually takes a few minutes. Please check back soon.
                    </p>
                    <div className="seller-action-row">
                      <button type="button" className="seller-action-btn" onClick={handleRefreshStatus}>
                        Refresh Status
                      </button>
                      <button type="button" className="seller-action-btn" onClick={handleSyncStatus}>
                        Sync from Veriff API
                      </button>
                      <button type="button" className="seller-action-btn seller-action-btn--danger" onClick={handleResetVerification}>
                        Reset Verification
                      </button>
                    </div>
                  </div>
                ) : verificationStatus === 'rejected' || verificationStatus === 'failed' ? (
                  <div className="seller-status-rejected">
                    <div className="seller-badge rejected">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
                        <path d="M15 9l-6 6M9 9l6 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <span>Verification Not Approved</span>
                    </div>
                    <p className="seller-status-text">
                      Your identity verification was not approved. This may be due to unclear documents, mismatched information, or other verification requirements.
                    </p>
                    <p className="seller-status-note" style={{ marginTop: '1rem', color: '#a0a0a0', fontSize: '14px' }}>
                      Please try again with clearer, high-quality photos of your ID documents. Make sure all information is clearly visible and matches your account details.
                    </p>
                    <div className="seller-action-row">
                      <button
                        type="button"
                        className="profile-become-seller-btn"
                        onClick={handleStartVerification}
                        disabled={verificationLoading}
                      >
                        {verificationLoading ? 'Starting...' : 'Try Again'}
                      </button>
                      <button
                        type="button"
                        className="seller-action-btn seller-action-btn--danger"
                        onClick={() => window.open('mailto:support@xmarket.gg?subject=Verification%20Support%20Request', '_blank')}
                      >
                        Contact Support
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="seller-verification-intro">
                    {verificationFormVisible ? (
                      <>
                        <p style={{ 
                          color: '#e5e5e5', 
                          marginBottom: '1rem',
                          fontSize: '16px',
                          fontFamily: 'Gilroy, sans-serif',
                          lineHeight: '1.6',
                          fontWeight: '500',
                          zIndex: 1,
                          position: 'relative'
                        }}>
                          Please fill out the form below to start verification:
                        </p>
                        <p style={{ 
                          color: '#a0a0a0', 
                          marginBottom: '1.5rem',
                          fontSize: '14px',
                          fontFamily: 'Gilroy, sans-serif',
                          lineHeight: '1.5',
                          zIndex: 1,
                          position: 'relative'
                        }}>
                          Veriff is an identity verification provider that helps companies connect with customers. Your information is secure and will only be used for verification purposes.
                        </p>
                        <div 
                          id="veriff-root" 
                          className="veriff-form-container"
                          style={{
                            display: 'block',
                            width: '100%',
                            minHeight: '600px',
                            height: 'auto',
                            padding: '2rem',
                            backgroundColor: '#141414',
                            border: '1px solid #252525',
                            borderRadius: '8px',
                            marginTop: '1rem',
                            position: 'relative',
                            zIndex: 1,
                            overflow: 'visible'
                          }}
                        ></div>
                        <button
                          onClick={handleCancelVerification}
                          style={{
                            marginTop: '1rem',
                            padding: '0.75rem 1.5rem',
                            backgroundColor: 'transparent',
                            color: '#a0a0a0',
                            border: '1px solid #252525',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontFamily: 'Gilroy, sans-serif',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#e5e5e5';
                            e.target.style.borderColor = '#353535';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = '#a0a0a0';
                            e.target.style.borderColor = '#252525';
                          }}
                        >
                          Cancel Verification
                        </button>
                      </>
                    ) : (
                      <div className="seller-intro-content">
                        <h4>Become a Verified Seller</h4>
                        <p>
                          Verify your identity to start selling on Bazaar.gg. This quick process helps us ensure 
                          a safe marketplace for everyone.
                        </p>
                        <div className="seller-benefits">
                          <h5>Benefits of becoming a seller:</h5>
                          <ul>
                            <li>List and sell products</li>
                            <li>Manage your inventory</li>
                            <li>Access seller features</li>
                            <li>Build your seller reputation</li>
                          </ul>
                        </div>
                        <button 
                          className="profile-become-seller-btn"
                          onClick={handleStartVerification}
                          disabled={verificationLoading}
                        >
                          {verificationLoading ? 'Starting Verification...' : 'Become a Seller'}
                        </button>
                        {verificationStatus === 'pending' && (
                          <button 
                            onClick={handleSyncStatus}
                            style={{
                              marginTop: '10px',
                              background: 'rgba(115, 83, 235, 0.1)',
                              border: '1px solid rgba(115, 83, 235, 0.3)',
                              color: '#7353eb',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontFamily: 'Gilroy, sans-serif',
                            }}
                          >
                            Sync Status from Veriff
                          </button>
                        )}
                        <p className="seller-privacy-note">
                          Your information is secure and will only be used for verification purposes.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'account' && (
              <div className="profile-section profile-section--bare">
                <div className="account-settings-panel">
                  <div className="account-setting-row">
                    <div className="account-setting-meta">
                      <label className="account-setting-label">{t('profile.emailLabel') || 'Email'}</label>
                      <p className="account-setting-description">
                        {t('profile.emailDescription') || 'Linked to your account. Not visible to other users.'}
                      </p>
                    </div>
                    <div className="account-setting-controls">
                      <span className="account-setting-value">{email || '—'}</span>
                      <button
                        type="button"
                        className="account-edit-link"
                        onClick={() => {
                          showToast(t('profile.emailEditingComingSoon') || 'Email editing coming soon', 'info');
                        }}
                      >
                        {t('profile.edit') || 'Edit'}
                      </button>
                    </div>
                  </div>

                  <div className="account-setting-row">
                    <div className="account-setting-meta">
                      <label className="account-setting-label">{t('profile.usernameLabel') || 'Username'}</label>
                      <p className="account-setting-description">
                        {t('profile.usernameDescription') || 'Visible to other users. Changeable once every 90 days.'}
                      </p>
                    </div>
                    <div className="account-setting-controls">
                      {!editing ? (
                        <>
                          <span className="account-setting-value">{username || '—'}</span>
                          <button
                            type="button"
                            className="account-edit-link"
                            onClick={() => setEditing(true)}
                          >
                            {t('profile.edit') || 'Edit'}
                          </button>
                        </>
                      ) : (
                        <div className="account-setting-edit">
                          <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="account-setting-input is-editing"
                            autoFocus
                          />
                          <div className="account-edit-actions">
                            <button type="button" className="account-save-link" onClick={handleSaveProfile}>
                              {t('profile.save') || 'Save'}
                            </button>
                            <button
                              type="button"
                              className="account-cancel-link"
                              onClick={() => {
                                setEditing(false);
                                setUsername(user?.username || '');
                              }}
                            >
                              {t('profile.cancel') || 'Cancel'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="account-setting-row">
                    <div className="account-setting-meta">
                      <label className="account-setting-label">{t('profile.passwordLabel') || 'Password'}</label>
                      <p className="account-setting-description">
                        {t('profile.passwordDescription') || 'Only for email/password login accounts.'}
                      </p>
                    </div>
                    <div className="account-setting-controls">
                      <button
                        type="button"
                        className="account-edit-link"
                        onClick={() => {
                          showToast(t('profile.passwordChangeComingSoon') || 'Password change coming soon', 'info');
                        }}
                      >
                        {t('profile.changePassword') || 'Change password'}
                      </button>
                    </div>
                  </div>

                  <div className="account-setting-row account-setting-row--avatar">
                    <div className="account-setting-meta">
                      <label className="account-setting-label">{t('profile.profilePicture') || 'Profile picture'}</label>
                      <p className="account-setting-description">
                        {t('profile.profilePictureDescription') || 'JPEG, PNG or HEIC · max 10MB'}
                      </p>
                    </div>
                    <div className="account-setting-controls">
                      <div className="account-profile-picture-section">
                        <div className="account-profile-picture-wrapper">
                          {getAvatarDisplay()}
                        </div>
                        <div className="account-profile-picture-actions">
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                          />
                          <button
                            type="button"
                            className="account-upload-image-btn"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            {t('profile.uploadImage') || 'Upload image'}
                          </button>
                          {avatarPreview && (
                            <div className="account-edit-actions">
                              <button type="button" className="account-save-link" onClick={handleSaveAvatar}>
                                {t('profile.save') || 'Save'}
                              </button>
                              <button
                                type="button"
                                className="account-cancel-link"
                                onClick={() => {
                                  setAvatarPreview(null);
                                  fileInputRef.current.value = '';
                                }}
                              >
                                {t('profile.cancel') || 'Cancel'}
                              </button>
                            </div>
                          )}
                          {!avatarPreview && user?.avatar_url && !user.avatar_url.startsWith('letter:') && (
                            <button type="button" className="account-remove-link" onClick={handleRemoveAvatar}>
                              {t('profile.remove') || 'Remove'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="profile-section">
                <div className="profile-empty-state">
                  <p>{t('profile.securitySettingsComingSoon') || 'Security settings coming soon...'}</p>
                </div>
              </div>
            )}

            {activeTab === 'purchased-orders' && (
              <div className="profile-section profile-section--bare">
                <BuyerOrders />
              </div>
            )}

            {activeTab === 'sold-orders' && (
              <div className="profile-section profile-section--bare">
                <SellerOrders />
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="profile-section profile-section--flush">
                <NotificationsTab userId={user?.id} />
              </div>
            )}

            {activeTab === 'wallet' && (
              <div className="profile-section profile-section--flush">
                <WalletTab />
              </div>
            )}

            {activeTab?.startsWith('offers-') && (
              <div className="profile-section profile-section--flush">
                <OffersTab />
              </div>
            )}

            {activeTab?.startsWith('seller-') && (
              <div className="profile-section profile-section--flush">
                <OffersTab />
              </div>
            )}

            {(activeTab === 'boosting' || activeTab === 'my-boosting-requests' || activeTab === 'received-boosting-requests') && (
              <div className="profile-section profile-section--bare">
                <BoostingManagement initialTab={activeTab === 'my-boosting-requests' ? 'my-requests' : 'received'} />
              </div>
            )}

            {activeTab === 'feedback' && (
              <div className="profile-section profile-section--flush">
                <ProfileFeedbackTab userId={user?.id} />
              </div>
            )}

            {activeTab === 'messages' && (
              <div className="profile-section profile-section--bare profile-messages-wrapper">
                <MessagesTab />
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="profile-section profile-section--bare">
                <div className="profile-view-panel">
                  <div className="profile-view-avatar">
                    {getAvatarDisplay()}
                  </div>
                  <div className="profile-view-info">
                    <div className="profile-view-heading">
                      <h2>{user.username || 'User'}</h2>
                    {user?.is_seller && (
                      <VerifiedSellerBadge>{t('profile.verifiedSeller') || 'Verified seller'}</VerifiedSellerBadge>
                    )}
                    </div>
                    <p className="profile-view-email">{user.email}</p>
                    <p className="profile-view-registered">
                      {t('profile.registered') || 'Registered'} · {user.created_at
                        ? new Date(user.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'N/A'}
                    </p>
                    <button
                      type="button"
                      className="view-public-profile-btn"
                      onClick={() => navigate(`/users/${user.username}`)}
                    >
                      {t('profile.viewPublicProfile') || 'View public profile'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  );
}

export default function ProfilePage() {
  return React.createElement(ProfilePageContent, null);
}